import {
  Stack,
  useFocusEffect,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import React, { useEffect, useMemo } from 'react';
import { Platform, ScrollView, View } from 'react-native';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';

import { useCustomer } from '@/api/customers/use-customer';
import { useReservation } from '@/api/reservations/use-reservation';
import {
  findAdditionalDriver,
  isAdditionalDriverVerified,
} from '@/components/additional-drivers/status';
import BackButton from '@/components/back-button';
import { FinishProfileModal } from '@/components/checkout/finish-profile-modal';
import ListItemStatus from '@/components/list-item-status';
import { useModalStore } from '@/components/login-modal/store';
import {
  NavigationButton,
  NavigationButtonTypes,
} from '@/components/navigation-button';
import { isDesktop, useAuth } from '@/core';
import mixpanelService from '@/core/clients/mixpanel';
import { Button, Text } from '@/ui';
import { Icon } from '@/ui/icon';
import { Cancel } from '@/ui/icons/cancel';
import { Checkmark } from '@/ui/icons/checkmark';
import { Hourglass } from '@/ui/icons/hourglass';
import { PendingCheckmarkBubble } from '@/ui/icons/pending-checkmark-bubble';
import { Loader } from '@/ui/loader';

export default function VerifyAdditionalDriver() {
  const { id } = useLocalSearchParams();
  const { styles, theme } = useStyles(stylesheet);
  const router = useRouter();
  const status = useAuth.use.status();
  const loginModalMode = useModalStore((state) => state.loginModalMode);
  const [showFinishProfileModal, setShowFinishProfileModal] =
    React.useState(false);

  const {
    data: resData,
    isPending: isFetchingReservation,
    refetch,
  } = useReservation({
    //@ts-ignore
    variables: { id: id },
    enabled: !!id,
  });

  const { data: customer } = useCustomer({ enabled: status === 'signIn' });

  useFocusEffect(
    React.useCallback(() => {
      refetch();
    }, [refetch])
  );

  useEffect(() => {
    if (loginModalMode === 'none') {
      refetch();
    }
  }, [loginModalMode, refetch]);

  useEffect(() => {
    if (status === 'signIn') {
      refetch();
    }
  }, [status, refetch]);

  useEffect(() => {
    mixpanelService.track('Page Viewed', {
      'Page Name': 'Verify Your Details',
    });
  }, []);

  const additionalDriver = useMemo(() => {
    if (!customer || !resData) return null;

    return findAdditionalDriver(resData?.additionalDrivers, customer);
  }, [resData, customer]);

  const checklistItems = React.useMemo(() => {
    if (!resData || !additionalDriver) return [];

    const subtext = (status: any) => {
      switch (status) {
        case 'pending':
          return (
            <Text type="caption1">Your verification is under review.</Text>
          );
        case 'rejected':
          return (
            <Text type="caption1" style={{ color: theme.colors.error }}>
              Your verification was rejected. Try again or contact customer
              support.
            </Text>
          );
        default:
          return '';
      }
    };

    const items = [
      {
        status: additionalDriver.driversLicenseStatus,
        text: "Driver's License",
        subtext: subtext(additionalDriver.driversLicenseStatus),
        visible: additionalDriver.driversLicenseStatus !== 'approved',
        onPress: () => {
          router.push(`/account/verify-license?trip_id=${resData.id}`);
        },
      },
      {
        status: additionalDriver.setupComplete === false && 'pending',
        text: 'Complete profile',
        buttonText: 'Complete',
        visible: !additionalDriver.setupComplete,
        onPress: () => {
          setShowFinishProfileModal(true);
        },
      },
    ];

    return items;
  }, [resData, additionalDriver, router, theme]);

  useEffect(() => {
    if (!resData || !additionalDriver) return;
    if (!isAdditionalDriverVerified(additionalDriver)) return;

    router.navigate(`/trips/${resData.id}/details`);
  }, [additionalDriver, resData, router]);

  // Everything the driver can do is done; they're waiting on review.
  const isSubmitted =
    !!additionalDriver &&
    !isAdditionalDriverVerified(additionalDriver) &&
    checklistItems.every((item) => !item.visible);

  if (isFetchingReservation || !resData) {
    return (
      <View
        style={{
          width: '100%',
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: theme.colors.background_1,
        }}
      >
        <Loader style={{ width: 150, height: 150 }} />
      </View>
    );
  }

  return (
    <View
      style={{
        flexGrow: 1,
        gap: 32,
        alignItems: 'center',
        backgroundColor: theme.colors.background_1,
      }}
    >
      {Platform.OS === 'web' && isDesktop() ? (
        <Stack.Screen
          options={{
            headerShown: false,
          }}
        />
      ) : (
        <Stack.Screen
          options={{
            title: 'Verify Your Details',
            headerTitle: 'Verify Your Details',
            headerLeft: () => (
              <BackButton
                onPress={() => {
                  router.replace('/trips');
                }}
              />
            ),
            headerShown: true,
            headerStyle: {
              backgroundColor: theme.colors.background_1,
            },
            headerTintColor: theme.colors.text.primary,
            headerShadowVisible: false,
            headerTitleStyle: {
              fontSize: 17,
            },
          }}
        />
      )}

      <View style={styles.container}>
        {['md', 'lg'].includes(UnistylesRuntime.breakpoint) ? (
          <View style={{ gap: 16 }}>
            <NavigationButton
              type={NavigationButtonTypes.back_with_text}
              onPress={() => {
                router.navigate('/trips');
              }}
            />

            <Text type="largeTitle">Verify Your Details</Text>
          </View>
        ) : null}

        {isSubmitted ? (
          <View style={styles.submitted}>
            <View style={styles.submittedMessage}>
              <Icon
                type="naked"
                icon={<PendingCheckmarkBubble />}
                fillColor={theme.colors.success}
                width={40}
                height={40}
              />
              <Text type="title2">Details Submitted</Text>
              <Text
                type="bodyRegular"
                style={{ color: theme.colors.text.secondary }}
              >
                We'll notify you when you're approved.
              </Text>
            </View>

            <Button
              text="Back To Trips"
              type="primary"
              onPress={() => {
                router.navigate('/trips');
              }}
            />
          </View>
        ) : (
          <ScrollView
            showsVerticalScrollIndicator={false}
            contentContainerStyle={styles.scrollContent}
            style={styles.scrollView}
          >
            <View style={{ flexGrow: 1, gap: 24 }}>
              <View style={{ gap: 8 }}>
                <Text type="title2">
                  {`${
                    resData?.customer.firstName ?? resData?.customer.firstname
                  }`}{' '}
                  invited you to this trip.
                </Text>

                <Text
                  type="bodyRegular"
                  style={{ color: theme.colors.text.secondary }}
                >
                  Verify your details to have access to the car and trip
                  details.
                </Text>
              </View>

              {checklistItems.length > 0 ? (
                <View style={{ gap: 16 }}>
                  {checklistItems
                    .filter((item) => item.visible)
                    .map((item, index) => {
                      let icon;

                      switch (item.status) {
                        case 'upload':
                          icon = (
                            <Icon
                              type="naked"
                              icon={<Hourglass />}
                              fillColor={theme.colors.icon.color}
                              width={12}
                              height={12}
                            />
                          );
                          break;
                        case 'pending':
                          icon = (
                            <Icon
                              type="naked"
                              icon={<Hourglass />}
                              fillColor={theme.colors.icon.color}
                              width={12}
                              height={12}
                            />
                          );
                          break;
                        case 'approved':
                          icon = (
                            <Icon
                              type="naked"
                              icon={<Checkmark />}
                              stroke={theme.colors.success}
                              fillColor={theme.colors.success}
                              width={12}
                              height={12}
                            />
                          );
                          break;
                        case 'rejected':
                          icon = (
                            <Icon
                              type="naked"
                              icon={<Cancel />}
                              stroke={theme.colors.error}
                              fillColor={theme.colors.error}
                              width={12}
                              height={12}
                            />
                          );
                          break;
                      }

                      return (
                        <ListItemStatus
                          key={`checklist-item-${index}`}
                          icon={icon}
                          text={item.text}
                          subtext={item.subtext}
                          buttonText={
                            item.buttonText ||
                            (['upload', 'rejected'].includes(item.status)
                              ? 'Verify'
                              : '')
                          }
                          onPress={() => {
                            item.onPress();
                          }}
                        />
                      );
                    })}
                </View>
              ) : null}
            </View>
          </ScrollView>
        )}
      </View>

      <FinishProfileModal
        visible={showFinishProfileModal}
        onClose={() => setShowFinishProfileModal(false)}
        onDone={() => {
          setShowFinishProfileModal(false);
          // Re-read the reservation so the driver's setupComplete flips; the
          // auto-advance effect then forwards to trip details once complete.
          refetch();
        }}
      />
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    flexGrow: 1,
    paddingTop: 24,
    paddingLeft: 16,
    paddingRight: 16,
    gap: 16,
    backgroundColor: theme.colors.background_1,
  },
  scrollView: {
    width: '100%',
    height: '100%',
  },
  submitted: {
    flex: 1,
    paddingBottom: 16,
    width: {
      xs: '100%',
      md: 800,
    },
    alignSelf: 'center',
  },
  submittedMessage: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
  },
  scrollContent: {
    width: {
      xs: '100%',
      md: 800,
    },
    alignSelf: 'center',
  },
  maxAdditionalDriversModalContainer: {
    margin: 0,
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
    maxWidth: {
      xs: '100%',
      sm: 600,
      md: 800,
    },
    width: {
      xs: '100%',
      md: 700,
    },
  },
}));
